import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import SglContext from '../SglContext';
import './myInvoices.css';

const MyInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState({});
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [loadingCharges, setLoadingCharges] = useState({});
  const [invoiceCharges, setInvoiceCharges] = useState({});
  const Sgl = useContext(SglContext);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      
      // FN: don't show invoices for now
      // const response = await axios.get(`${Sgl.nestApiUrl}/billing/my-invoices`, {
      //   params: {
      //     user_id: Sgl.current_user?.sgl_id,
      //     company_id: Sgl.company_id
      //   }
      // });

      // if (response.data.success) {
      //   setInvoices(response.data.invoices || []);
      // } else {
      //   setError(response.data.error || 'Failed to load invoices');
      // }

      setInvoices([]);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to load invoices';
      setError(errorMessage);
      console.error('Error loading invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice, event) => {
    event.stopPropagation();
    const invoiceId = invoice.id;
    
    try {
      setGeneratingPdf(prev => ({ ...prev, [invoiceId]: true }));
      
      const response = await axios.post(`${Sgl.nestApiUrl}/billing/generate-invoice-pdf`, {
        invoice_id: invoiceId,
        invoice_type_id: invoice.invoice_type_id,
        company_id: Sgl.company_id
      });

      if (response.data.success && response.data.pdfUrl) {
        window.open(response.data.pdfUrl, '_blank');
      } else {
        console.error('Failed to generate PDF:', response.data.error);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const loadInvoiceCharges = async (invoice) => {
    const invoiceId = invoice.id;
    
    if (invoiceCharges[invoiceId]) {
      return; 
    }

    try {
      setLoadingCharges(prev => ({ ...prev, [invoiceId]: true }));
      
      const response = await axios.get(`${process.env.REACT_APP_NEST_API}/billing/invoice-charges`, {
        params: {
          invoice_id: invoiceId,
          account_id: invoice.account_id || 0, 
          company_id: Sgl.company_id
        }
      });

      if (response.data.success) {
        setInvoiceCharges(prev => ({
          ...prev,
          [invoiceId]: response.data.charges || []
        }));
      } else {
        console.error('Failed to load charges:', response.data.error);
        setInvoiceCharges(prev => ({
          ...prev,
          [invoiceId]: []
        }));
      }
    } catch (error) {
      console.error('Error loading charges:', error);
      setInvoiceCharges(prev => ({
        ...prev,
        [invoiceId]: []
      }));
    } finally {
      setLoadingCharges(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const toggleExpand = (invoice) => {
    const invoiceId = invoice.id;
    const isCurrentlyExpanded = expandedInvoices[invoiceId];
    
    // Collapse all other invoices and expand/collapse the clicked one
    setExpandedInvoices({
      [invoiceId]: !isCurrentlyExpanded
    });

    // Load charges when expanding
    if (!isCurrentlyExpanded) {
      loadInvoiceCharges(invoice);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatChargeDate = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    
    const dateOnly = dateTimeString.split('T')[0] || dateTimeString.split(' ')[0];
    
    try {
      return new Date(dateOnly).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateTimeString; 
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const isUnpaid = (status) => {
    if (!status) return true;
    return status.toLowerCase() !== 'paid';
  };

  const isMobileView = () => {
    return window.innerWidth <= 640;
  };

  const isSmallScreen = () => {
    return window.innerWidth <= 580;
  };

const renderChargesTable = (charges, invoiceId) => {
  if (loadingCharges[invoiceId]) {
    return (
      <div className="loading-charges">
        <div className="spinner"></div>
        <p>Loading charges...</p>
      </div>
    );
  }

  if (!charges || charges.length === 0) {
    return (
      <div className="no-charges">
        <p>No charges found for this invoice.</p>
      </div>
    );
  }

  //  mobile charges in a scrollable container
  const chargesContent = (
    <div className="charges-table-container">
      <table className="charges-table">
        <thead>
          <tr className="charges-table-header">
            {/* Updated header structure */}
            <th className="display_on_desktop">Description</th>
            <th className="display_on_desktop">Rate</th>
            <th className="display_on_desktop">Tax</th>
            <th className="display_on_desktop">Total</th>
          </tr>
        </thead>
        <tbody>
          {charges.map((charge, index) => (
            <tr key={charge.sgl_id || index} className="charges-table-row">
              {/* Mobile card view */}
              <td className="display_on_mobile charges-mobile-card">
                <div className="charges-card-content">
                <div className="charges-row description-only">
                  <span className="charges-value description-full-width">{charge.description || 'N/A'}</span>
                </div>
                  <div className="charges-row">
                    <span className="charges-label">Rate:</span>
                    <span className="charges-value">{formatCurrency(charge.rate)}</span>
                  </div>
                  <div className="charges-row">
                    <span className="charges-label">Tax:</span>
                    <span className="charges-value">{formatCurrency(charge.tax)}</span>
                  </div>
                  <div className="charges-row">
                    <span className="charges-label">Total:</span>
                    <span className="charges-value">{formatCurrency(charge.total)}</span>
                  </div>
                </div>
              </td>
              
              {/* Desktop table view */}
              <td className="display_on_desktop">
                <div className="charges-description">{charge.description || 'N/A'}</div>
              </td>
              <td className="display_on_desktop">
                <div className="charges-rate amount-cell">{formatCurrency(charge.rate)}</div>
              </td>
              <td className="display_on_desktop">
                <div className="charges-tax amount-cell">{formatCurrency(charge.tax)}</div>
              </td>
              <td className="display_on_desktop">
                <div className="charges-total amount-cell">{formatCurrency(charge.total)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Check if we're in mobile view and have multiple charges to wrap in mobile scroll container
  if (isMobileView() && charges.length > 3) {
    return (
      <div className="mobile-charges-container">
        {chargesContent}
      </div>
    );
  }

  return chargesContent;
};

  const renderInvoiceCard = (invoice) => {
    const isGenerating = generatingPdf[invoice.id];
    const showStatus = isUnpaid(invoice.status);
    const isExpanded = expandedInvoices[invoice.id];
    const charges = invoiceCharges[invoice.id];
    const smallScreen = isSmallScreen();
    
    return (
      <div 
        key={invoice.id} 
        className={`invoice-card ${isExpanded ? 'expanded' : ''}`}
      >
        <div className="invoice-header" onClick={() => toggleExpand(invoice)}>
          {/* Expand icon positioned absolutely on small screens */}
          {smallScreen && (
            <div className="invoice-expand-container">
              <svg className="expand-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </div>
          )}

          <div className="invoice-header-content">
            <div className="invoice-left">
              {/* Row 1: Invoice Title + Status */}
              <div className="invoice-title-row">
                <h4 className="invoice-title">#{invoice.id}</h4>
                {showStatus && (
                  <span className="invoice-status unpaid">Unpaid</span>
                )}
              </div>
              
              {/* Row 2: Date */}
              <div className="invoice-date-row">
                <svg className="date-icon" width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span className="invoice-date">{formatDate(invoice.date)}</span>
              </div>
              
              {/* Row 3: Description on small screens */}
              {!smallScreen && invoice.description && (
                <div className="invoice-description-row">
                  <p className="invoice-description">{invoice.description}</p>
                </div>
              )}
            </div>
            
            {/* Different layout for small vs larger screens */}
            {!smallScreen ? (
              <div className="invoice-right">
                {/* Row 1 Right: Arrow */}
                <svg className="expand-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
                
                {/* Row 2 Right: Amount */}
                <div className="invoice-amount">
                  <div className="invoice-amount-value">{formatCurrency(invoice.amount)}</div>
                </div>
                
                {/* Row 3 Right: Download Link */}
                <a 
                  href="#" 
                  className="download-link"
                  onClick={(e) => handleDownloadPdf(invoice, e)}
                >
                  {isGenerating ? 'Generating...' : 'Download PDF'}
                </a>
              </div>
            ) : (
              /* Small screen layout: Description then Amount/Download stacked below */
              <>
                {/* Description row - full width */}
                {invoice.description && (
                  <div className="invoice-description-row">
                    <p className="invoice-description">{invoice.description}</p>
                  </div>
                )}
                
                {/* Amount and Download vertically stacked below */}
                <div className="invoice-right">
                  {/* Amount */}
                  <div className="invoice-amount">
                    <div className="invoice-amount-value">{formatCurrency(invoice.amount)}</div>
                  </div>
                  
                  {/* Download Link */}
                  <a 
                    href="#" 
                    className="download-link"
                    onClick={(e) => handleDownloadPdf(invoice, e)}
                  >
                    {isGenerating ? 'Generating...' : 'Download PDF'}
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div className="invoice-details">
            {isMobileView() && <h5>Invoice Charges</h5>}
            {renderChargesTable(charges, invoice.id)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="my-invoices">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your invoices...</p>
        </div>
      </div>
    );
  }

  if (error && false) { // FN for now don't display the error
    return (
      <div className="my-invoices">
        <div className="invoices-header">
          <h2>My Invoices</h2>
        </div>
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={loadInvoices} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="my-invoices">
        <div className="invoices-header">
          <h2>My Invoices</h2>
        </div>
        <div className="no-invoices">
          <div className="no-invoices-icon">📄</div>
          <h3>No invoices found</h3>
          <p>You don't have any invoices at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-invoices">
      <div className="invoices-header">
        <h2>My Invoices</h2>
      </div>

      <div className="invoices-list">
        {invoices.map(invoice => renderInvoiceCard(invoice))}
      </div>
    </div>
  );
};

export default MyInvoices;